import React from 'react';
import { Alert, List, Space, Tag, Typography } from 'antd';
import { WarningOutlined } from '@ant-design/icons';

const { Text, Paragraph } = Typography;

/**
 * Renders `data.implications` from GET /v2/pa/{id}/checkout-validation
 * (and from the checkout completion response) exactly as the API sends it.
 *
 * The API returns this block when an ADMIN actor checks out an encounter whose
 * PA items were all rejected: the encounter is not served as a claim, it is
 * deleted (reversibly). Every string below comes from the server — headline,
 * consequence title/detail, and the reversal effect/limitation. Nothing here is
 * invented client-side, and the server orders `consequences` so the released
 * benefit allowance and the double-claim risk come first; render them in the
 * order given.
 *
 * `reversal.route` / `.path` / `.preview` / `.mode` are machine-facing: they
 * exist so a Restore action can be wired to them. They are never rendered as
 * text — an admin reading this is deciding whether to delete an encounter, and
 * an HTTP verb, a path template or a permission slug does not inform that
 * decision. "Only an admin can restore this" is carried by the server prose.
 */

const SEVERITY_TAG = {
  high: { color: 'error', label: 'HIGH' },
  medium: { color: 'warning', label: 'MEDIUM' },
  low: { color: 'default', label: 'LOW' },
};

const CheckoutImplications = ({ implications, style }) => {
  if (!implications || typeof implications !== 'object') return null;

  const consequences = Array.isArray(implications.consequences)
    ? implications.consequences
    : [];
  const reversal =
    implications.reversal && typeof implications.reversal === 'object'
      ? implications.reversal
      : {};

  return (
    <div style={style}>
      {implications.headline ? (
        <Alert
          type="error"
          showIcon
          icon={<WarningOutlined />}
          message="What this does"
          description={implications.headline}
          style={{ marginBottom: '12px' }}
        />
      ) : null}

      {consequences.length > 0 ? (
        <List
          size="small"
          bordered
          dataSource={consequences}
          style={{ marginBottom: '12px', background: '#fff' }}
          renderItem={(item) => {
            const severity =
              SEVERITY_TAG[String(item?.severity || '').toLowerCase()] || {
                color: 'default',
                label: String(item?.severity || 'INFO').toUpperCase(),
              };
            return (
              <List.Item key={item?.key || item?.title}>
                <Space direction="vertical" size={2} style={{ width: '100%' }}>
                  <Space size={8} align="start">
                    <Tag color={severity.color} style={{ marginInlineEnd: 0 }}>
                      {severity.label}
                    </Tag>
                    <Text strong>{item?.title}</Text>
                  </Space>
                  {item?.detail ? (
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      {item.detail}
                    </Text>
                  ) : null}
                </Space>
              </List.Item>
            );
          }}
        />
      ) : null}

      <Alert
        type={implications.reversible ? 'info' : 'error'}
        showIcon
        message={implications.reversible ? 'Reversible' : 'Not reversible'}
        description={
          <Space direction="vertical" size={4} style={{ width: '100%' }}>
            {reversal.effect ? (
              <Paragraph style={{ margin: 0 }}>{reversal.effect}</Paragraph>
            ) : null}
            {reversal.limitation ? (
              <Paragraph style={{ margin: 0 }}>
                <Text strong>Limitation: </Text>
                {reversal.limitation}
              </Paragraph>
            ) : null}
          </Space>
        }
      />
    </div>
  );
};

export default CheckoutImplications;
